import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  BarChartOutlined,
  FileDownloadOutlined,
  PercentOutlined,
  SavingsOutlined,
  TrendingDown,
  TrendingUp,
} from "@mui/icons-material";
import MfReportTableSearch from "./MfReportTableSearch";
import MfReportPdfDownloadButton from "./MfReportPdfDownloadButton";
import {
  REPORT_COLORS,
  REPORT_FROZEN_TABLE_CARD_HEADER_SX,
  REPORT_FROZEN_TABLE_CONTAINER_SX,
  REPORT_FROZEN_TABLE_OUTER_CARD_SX,
  REPORT_PRIMARY_BUTTON_SX,
  REPORT_SUMMARY_CARD_SX,
  REPORT_TYPO,
} from "./mfReportsTheme";
import {
  isSummableReportColumn,
  parseReportCsvNumeric,
  parseReportTotalHoldingSummary,
} from "../../../utils/reports/reportCsvTotals";
import {
  formatSignedReturnMetricPercent,
  getDisplayableReturnMetric,
  shouldDisplayReturnMetricAsDash,
} from "../../../utils/returnMetrics";
import { PROFILE_FONT } from "../../../constants/layout";
import { HOLDINGS_PAGE_SIZE } from "../../../utils/holdings/holdingsPagination";

type FieldColumn = { key: string; value: string };

const PORTFOLIO_CAGR_KEYS_BY_SEGMENT: Record<string, readonly string[]> = {
  "Mutual Funds": ["cagrMF", "cagrMFAumCapital", "cagr"],
  PMS: ["cagrPms", "cagr"],
  STOCK: ["cagrStock", "cagr"],
  PRIVATE_STOCK: ["cagrPrivateStock", "cagr"],
  AIF: ["cagrAif", "cagr"],
  Bonds: ["cagr", "cagrBond"],
  REIT: ["cagrReit", "cagr"],
  "International Equity": ["cagr", "cagrIntlEquity"],
  INTERNATIONAL_EQUITY: ["cagr", "cagrIntlEquity"],
  Insurance: ["cagrInsurance", "cagr"],
  "Other Asset": ["cagrOther", "cagr"],
  "Corporate FD": ["cagrFD", "cagr"],
};

const isHoldingStyleCagrReportType = (type?: string): boolean => {
  const rt = String(type ?? "").trim();
  return rt === "Holding" || rt === "Holdings" || rt === "All";
};

const segmentSupportsPortfolioCagrCard = (seg?: string): boolean =>
  seg !== "SIF" && Boolean(seg && seg in PORTFOLIO_CAGR_KEYS_BY_SEGMENT);

type Props = {
  loading?: boolean;
  rowData: Record<string, unknown>[];
  controlBarObj: FieldColumn[];
  segment?: string;
  reportType?: string;
  reportTitle?: string;
  totalHoldingInfo?: Record<string, unknown> | null;
  taxReportTotalGainLoss?: number | null;
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  totalUnfilteredCount: number;
  tableScrollRef?: React.Ref<HTMLDivElement>;
  showPagination?: boolean;
  pageIndex?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onExportCsv: () => void;
  onExportPdf: () => void;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  children: React.ReactNode;
};

const formatReportInr = (value: number) =>
  `₹${Math.abs(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const isTransactionReportType = (type?: string): boolean => {
  const rt = String(type ?? "").trim().toLowerCase();
  return rt === "transaction" || rt === "transaction report";
};

const isBreakdownReportType = (type?: string): boolean => {
  const rt = String(type ?? "").trim().toLowerCase();
  return (
    rt === "sector report" ||
    rt === "cap report" ||
    rt === "consolidated report"
  );
};

const isRealizedReportType = (type?: string): boolean =>
  String(type ?? "").trim().toLowerCase() === "realized";

const isPointToPointReportType = (type?: string): boolean =>
  String(type ?? "").trim().toLowerCase() === "point to point return";

const isStockTaxReportType = (type?: string): boolean => {
  const rt = String(type ?? "").trim();
  return (
    rt === "Intraday" ||
    rt === "Short Term Capital Gain" ||
    rt === "Long Term Capital Gain" ||
    rt === "Tax Report"
  );
};

const resolveReportTableTitle = (type?: string): string => {
  const rt = String(type ?? "").trim();
  if (rt === "Holding") return "Unrealized Report";
  if (rt === "Realized") return "Realized Report";
  if (rt === "All") return "All Report";
  if (rt === "Transaction report") return "Transaction Details";
  if (rt === "Transaction") return "Transaction Report";
  if (rt === "Cap report") return "Market Cap Report";
  if (rt === "Sector report") return "Sector Report";
  if (rt === "Point to Point Return") return "Point to Point Return";
  if (rt === "Weekly Returns") return "Weekly Returns";
  if (rt === "MIS Report") return "MIS Report";
  if (rt === "Tax Report") return "Tax Report";
  return rt || "Holdings Report";
};

const MfProfessionalReportTable: React.FC<Props> = ({
  loading = false,
  rowData,
  controlBarObj,
  segment,
  reportType,
  reportTitle: reportTitleOverride,
  totalHoldingInfo = null,
  taxReportTotalGainLoss = null,
  searchQuery,
  onSearchQueryChange,
  totalUnfilteredCount,
  tableScrollRef,
  showPagination = false,
  pageIndex = 1,
  totalPages = 1,
  onPageChange,
  onExportCsv,
  onExportPdf,
  onScroll,
  children,
}) => {
  const reportTitle = reportTitleOverride?.trim() || resolveReportTableTitle(reportType);
  const schemeCount = totalUnfilteredCount ?? rowData.length;
  const showSearch = schemeCount > 0;

  const shouldShowSummaryCards =
    rowData.length > 0 &&
    !isTransactionReportType(reportType) &&
    !isBreakdownReportType(reportType) &&
    !isPointToPointReportType(reportType) &&
    !isStockTaxReportType(reportType) &&
    !isRealizedReportType(reportType);

  const summary = useMemo(() => {
    const tableColumns = controlBarObj ?? [];

    const isPercentLikeColumn = (key: string, label: string) => {
      const keyLower = key.toLowerCase();
      if (
        keyLower === "roi" ||
        keyLower.includes("combined") ||
        keyLower.includes("benchmark")
      ) {
        return true;
      }
      return (
        /perc|percentage|xirr|cagr|%/i.test(key) ||
        /perc|percentage|xirr|cagr|return\s*%|interest\s*rate|%/i.test(label)
      );
    };

    const isNonMetricColumn = (key: string) => {
      const keyLower = key.toLowerCase();
      return (
        keyLower.includes("combined") ||
        keyLower.includes("benchmark") ||
        keyLower === "cagr" ||
        keyLower === "xirr"
      );
    };

    /** Map visible table columns to invested / current / P&L (same rules as CSV totals). */
    const getTableMetricColumnKeys = (
      metric: "invested" | "current" | "pnl",
    ): string[] =>
      tableColumns
        .filter((col) => {
          if (!isSummableReportColumn(col)) return false;
          const key = col.key.toLowerCase();
          const label = String(col.value ?? "").toLowerCase();
          if (isPercentLikeColumn(col.key, label)) return false;
          if (isNonMetricColumn(col.key)) return false;

          if (metric === "invested") {
            return (
              key.includes("invested") ||
              key === "costvalue" ||
              key.includes("purchasevalue") ||
              (key === "amount" &&
                !/current|gain|pnl|market|maturity/i.test(label)) ||
              /invested|purchase\s*value|cost\s*value|net\s*purchase|investment\s*amount/i.test(
                label,
              )
            );
          }
          if (metric === "current") {
            return (
              (key.includes("current") &&
                !key.includes("nav") &&
                !key.includes("date") &&
                !key.includes("price")) ||
              key.includes("marketvalue") ||
              key.includes("maturityamount") ||
              key.includes("returnamount") ||
              /current\s*value|current\s*amt|^current$|market\s*value|maturity\s*amount/i.test(
                label,
              )
            );
          }
          return (
            /pnl|gainloss|gain.?loss|absolutepl|absolutepnl|absolutereturn|unrealisedpnl|unrealizedpnl|pandl/i.test(
              key,
            ) ||
            /gain\s*\/?\s*loss|p\s*&\s*l|absolute\s*p|absolute\s*return(?!\s*%)/i.test(
              label,
            )
          );
        })
        .map((col) => col.key);

    const sumTableRowsForKeys = (keys: string[]): number => {
      if (!keys.length) return 0;
      let total = 0;
      for (const row of rowData) {
        for (const key of keys) {
          const parsed = parseReportCsvNumeric(row?.[key]);
          if (parsed !== null) {
            total += parsed;
            break;
          }
        }
      }
      return total;
    };

    const investedKeys = getTableMetricColumnKeys("invested");
    const currentKeys = getTableMetricColumnKeys("current");
    const pnlKeys = getTableMetricColumnKeys("pnl");

    const hasInvestedMetric = investedKeys.length > 0;
    const hasCurrentMetric = currentKeys.length > 0;
    const hasPnlColumns = pnlKeys.length > 0;
    const canDerivePnlFromCurrent =
      hasInvestedMetric && hasCurrentMetric && !hasPnlColumns;
    const rowPnlSum = sumTableRowsForKeys(pnlKeys);

    const apiTotals = parseReportTotalHoldingSummary(totalHoldingInfo);

    const readPortfolioCagr = (): number | null => {
      if (!totalHoldingInfo) return null;
      const keys = PORTFOLIO_CAGR_KEYS_BY_SEGMENT[segment ?? ""] ?? ["cagr"];
      for (const key of keys) {
        const displayable = getDisplayableReturnMetric(
          totalHoldingInfo[key] as string | number | null | undefined,
        );
        if (displayable !== null) return displayable;
      }
      return null;
    };

    let totalInvested: number;
    let currentValue: number;
    let totalPnl: number;
    let overallReturnPct: number;
    let canShowReturn: boolean;
    let totalCagr: number | null;
    let hasCagrMetric: boolean;

    if (apiTotals) {
      totalInvested =
        apiTotals.totalInvested !== null
          ? apiTotals.totalInvested
          : sumTableRowsForKeys(investedKeys);
      currentValue =
        apiTotals.currentValue !== null
          ? apiTotals.currentValue
          : sumTableRowsForKeys(currentKeys);
      totalPnl =
        apiTotals.totalPnl !== null
          ? apiTotals.totalPnl
          : hasPnlColumns
            ? rowPnlSum
            : currentValue - totalInvested;
      overallReturnPct =
        apiTotals.overallReturnPct !== null
          ? apiTotals.overallReturnPct
          : totalInvested > 0 && hasInvestedMetric && hasCurrentMetric
            ? ((currentValue - totalInvested) / totalInvested) * 100
            : 0;
      canShowReturn =
        totalInvested > 0 && hasInvestedMetric && hasCurrentMetric;
      totalCagr = getDisplayableReturnMetric(apiTotals.cagr) ?? readPortfolioCagr();
      hasCagrMetric = totalCagr !== null;
    } else {
      totalInvested = sumTableRowsForKeys(investedKeys);
      currentValue = sumTableRowsForKeys(currentKeys);
      totalPnl = hasPnlColumns
        ? rowPnlSum
        : canDerivePnlFromCurrent
          ? currentValue - totalInvested
          : rowPnlSum;
      overallReturnPct =
        totalInvested > 0 && hasInvestedMetric && hasCurrentMetric
          ? ((currentValue - totalInvested) / totalInvested) * 100
          : 0;
      canShowReturn =
        totalInvested > 0 && hasInvestedMetric && hasCurrentMetric;
      totalCagr = readPortfolioCagr();
      hasCagrMetric = totalCagr !== null;
    }

    const niftyCagr = getDisplayableReturnMetric(
      (apiTotals?.niftyCagr ?? totalHoldingInfo?.niftyCagr) as
        | string
        | number
        | null
        | undefined,
    );

    return {
      totalInvested,
      currentValue,
      totalPnl,
      overallReturnPct,
      totalCagr,
      hasCagrMetric,
      niftyCagr,
      canShowReturn,
    };
  }, [rowData, totalHoldingInfo, controlBarObj, segment]);

  const shouldShowTaxSummaryCards =
    isStockTaxReportType(reportType) &&
    taxReportTotalGainLoss !== null &&
    taxReportTotalGainLoss !== undefined;

  const taxSummaryCards = shouldShowTaxSummaryCards
    ? [
        {
          label: "Total Gain / Loss",
          value: `${taxReportTotalGainLoss >= 0 ? "+" : "-"}${formatReportInr(taxReportTotalGainLoss)}`,
          icon:
            taxReportTotalGainLoss < 0 ? (
              <TrendingDown sx={{ color: REPORT_COLORS.loss }} />
            ) : (
              <TrendingUp sx={{ color: REPORT_COLORS.profit }} />
            ),
          valueColor:
            taxReportTotalGainLoss >= 0
              ? REPORT_COLORS.profit
              : REPORT_COLORS.loss,
        },
      ]
    : [];

  const shouldShowCagrSummaryCard =
    segment !== "SIF" &&
    shouldShowSummaryCards &&
    (summary.hasCagrMetric ||
      (segmentSupportsPortfolioCagrCard(segment) &&
        isHoldingStyleCagrReportType(reportType)));

  const formatSummaryCagr = (cagr: number | null, hasMetric: boolean) =>
    hasMetric ? formatSignedReturnMetricPercent(cagr) : "-";

  /** P2P reports hide the 4 totals but still surface the portfolio CAGR card. */
  const p2pPortfolioCagr = isPointToPointReportType(reportType)
    ? getDisplayableReturnMetric(
        totalHoldingInfo?.cagr as string | number | null | undefined,
      )
    : null;
  const shouldShowP2pCagrCard =
    isPointToPointReportType(reportType) &&
    rowData.length > 0 &&
    p2pPortfolioCagr !== null;

  const p2pCagrCards = shouldShowP2pCagrCard
    ? [
        {
          label: "Total CAGR",
          value: formatSummaryCagr(p2pPortfolioCagr, true),
          icon:
            p2pPortfolioCagr === null ||
            shouldDisplayReturnMetricAsDash(p2pPortfolioCagr) ? (
              <TrendingUp sx={{ color: REPORT_COLORS.mutedText }} />
            ) : p2pPortfolioCagr < 0 ? (
              <TrendingDown sx={{ color: REPORT_COLORS.loss }} />
            ) : (
              <TrendingUp sx={{ color: REPORT_COLORS.profit }} />
            ),
          valueColor:
            p2pPortfolioCagr === null ||
            shouldDisplayReturnMetricAsDash(p2pPortfolioCagr)
              ? REPORT_COLORS.mutedText
              : p2pPortfolioCagr >= 0
                ? REPORT_COLORS.profit
                : REPORT_COLORS.loss,
        },
      ]
    : [];

  const summaryCards = shouldShowTaxSummaryCards
    ? taxSummaryCards
    : shouldShowP2pCagrCard
    ? p2pCagrCards
    : shouldShowSummaryCards
    ? [
        {
          label: "Total Invested",
          value: formatReportInr(summary.totalInvested),
          icon: <SavingsOutlined sx={{ color: REPORT_COLORS.button }} />,
        },
        {
          label: "Current Value",
          value: formatReportInr(summary.currentValue),
          icon: <AccountBalanceWalletOutlined sx={{ color: REPORT_COLORS.button }} />,
        },
        {
          label: "Total Gain / Loss",
          value: `${summary.totalPnl >= 0 ? "+" : "-"}${formatReportInr(summary.totalPnl)}`,
          icon:
            summary.totalPnl < 0 ? (
              <TrendingDown sx={{ color: REPORT_COLORS.loss }} />
            ) : (
              <TrendingUp sx={{ color: REPORT_COLORS.profit }} />
            ),
          valueColor:
            summary.totalPnl >= 0 ? REPORT_COLORS.profit : REPORT_COLORS.loss,
        },
        {
          label: "Overall Return",
          value: summary.canShowReturn
            ? `${summary.overallReturnPct >= 0 ? "+" : "-"}${Math.abs(summary.overallReturnPct).toFixed(2)}%`
            : "-",
          icon: (
            <PercentOutlined
              sx={{
                color: summary.canShowReturn
                  ? summary.overallReturnPct >= 0
                    ? REPORT_COLORS.profit
                    : REPORT_COLORS.loss
                  : REPORT_COLORS.mutedText,
              }}
            />
          ),
          valueColor: summary.canShowReturn
            ? summary.overallReturnPct >= 0
              ? REPORT_COLORS.profit
              : REPORT_COLORS.loss
            : REPORT_COLORS.mutedText,
        },
        ...(shouldShowCagrSummaryCard
          ? [
              {
                label: "Total CAGR",
                value: formatSummaryCagr(summary.totalCagr, summary.hasCagrMetric),
                icon:
                  !summary.hasCagrMetric ||
                  summary.totalCagr === null ||
                  shouldDisplayReturnMetricAsDash(summary.totalCagr) ? (
                    <TrendingUp sx={{ color: REPORT_COLORS.mutedText }} />
                  ) : summary.totalCagr < 0 ? (
                    <TrendingDown sx={{ color: REPORT_COLORS.loss }} />
                  ) : (
                    <TrendingUp sx={{ color: REPORT_COLORS.profit }} />
                  ),
                valueColor:
                  summary.hasCagrMetric &&
                  summary.totalCagr !== null &&
                  !shouldDisplayReturnMetricAsDash(summary.totalCagr)
                    ? summary.totalCagr >= 0
                      ? REPORT_COLORS.profit
                      : REPORT_COLORS.loss
                    : REPORT_COLORS.mutedText,
              },
            ]
          : []),
        {
          label: "Nifty 50 CAGR",
          value: formatSignedReturnMetricPercent(summary.niftyCagr),
          icon: shouldDisplayReturnMetricAsDash(summary.niftyCagr) ? (
            <TrendingUp sx={{ color: REPORT_COLORS.mutedText }} />
          ) : summary.niftyCagr != null && summary.niftyCagr < 0 ? (
            <TrendingDown sx={{ color: REPORT_COLORS.loss }} />
          ) : (
            <TrendingUp sx={{ color: REPORT_COLORS.profit }} />
          ),
          valueColor: shouldDisplayReturnMetricAsDash(summary.niftyCagr)
            ? REPORT_COLORS.mutedText
            : summary.niftyCagr != null && summary.niftyCagr < 0
              ? REPORT_COLORS.loss
              : REPORT_COLORS.profit,
        },
      ]
    : [];

  return (
    <Box sx={{ width: "100%", minWidth: 0, mt: { xs: 2, md: 3 }, mb: 2 }}>
      {showSearch && (
        <MfReportTableSearch
          value={searchQuery}
          onChange={onSearchQueryChange}
          matchCount={rowData.length}
          totalCount={schemeCount}
          disabled={loading}
        />
      )}

      {summaryCards.length > 0 && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <Card elevation={0} sx={REPORT_SUMMARY_CARD_SX}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography sx={{ ...REPORT_TYPO.sectionTitle, fontSize: 11, mb: 1.5 }}>
                      {card.label}
                    </Typography>
                    <Typography
                      fontSize={22}
                      fontWeight={700}
                      letterSpacing={-0.4}
                      color={(card as { valueColor?: string }).valueColor ?? REPORT_COLORS.primaryText}
                      sx={{ fontFamily: PROFILE_FONT }}
                    >
                      {card.value}
                    </Typography>
                  </Box>
                  {card.icon}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card elevation={0} sx={REPORT_FROZEN_TABLE_OUTER_CARD_SX}>
        <Box sx={REPORT_FROZEN_TABLE_CARD_HEADER_SX}>
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={0.75}>
              <BarChartOutlined sx={{ color: REPORT_COLORS.button, fontSize: 24 }} />
              <Typography sx={REPORT_TYPO.sectionHeading}>{reportTitle}</Typography>
            </Box>
            <Typography sx={{ ...REPORT_TYPO.body, color: REPORT_COLORS.mutedText, fontSize: 13 }}>
              {schemeCount} Scheme{schemeCount !== 1 ? "s" : ""}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <MfReportPdfDownloadButton
              columnCount={controlBarObj.length}
              hasRows={rowData.length > 0}
              onExport={onExportPdf}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownloadOutlined />}
              onClick={onExportCsv}
              disabled={!rowData.length}
              sx={{ ...REPORT_PRIMARY_BUTTON_SX, fontSize: 13, px: 2.5, py: 1 }}
            >
              CSV
            </Button>
          </Stack>
        </Box>

        <Box ref={tableScrollRef} onScroll={onScroll} sx={REPORT_FROZEN_TABLE_CONTAINER_SX}>
          {children}
        </Box>

        {showPagination && totalPages > 1 && !loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              py: 1,
              borderTop: `1px solid ${REPORT_COLORS.border}`,
              bgcolor: REPORT_COLORS.background,
            }}
          >
            <TablePagination
              component="div"
              count={-1}
              page={Math.max(0, pageIndex - 1)}
              onPageChange={(_, newPage) => onPageChange?.(newPage + 1)}
              rowsPerPage={HOLDINGS_PAGE_SIZE}
              rowsPerPageOptions={[]}
              labelRowsPerPage=""
              labelDisplayedRows={() => `Page ${pageIndex} of ${totalPages}`}
              backIconButtonProps={{ disabled: pageIndex <= 1 }}
              nextIconButtonProps={{ disabled: pageIndex >= totalPages }}
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default MfProfessionalReportTable;
